/**
 * Peck answer prompt.
 *
 * Original to this repository. It instructs the model to answer a question
 * strictly from a set of retrieved nest pages and to cite each claim with a
 * Logseq-style `[[slug]]` link. The BFF builds the prompt from structured page
 * data using this module so the payload to the Kip backend is deterministic.
 */

export interface PromptPage {
  slug: string;
  type?: string;
  /** the page body (frontmatter stripped, trimmed) */
  content: string;
}

/** Render candidate pages for the prompt: one labelled block per page. */
export function formatPagesForPrompt(pages: PromptPage[]): string {
  return pages
    .map((p) => `### Page: ${p.slug} (type: ${p.type || 'unknown'})\n${p.content}`)
    .join('\n\n---\n\n');
}

export const ANSWER_SYSTEM_PROMPT = `You answer questions about someone's personal notes. Below are the full contents of the notes pages that a search turned up for this question.

Use only what those pages say. Do not add outside knowledge and do not guess past what is written. If the pages don't cover the question, say that directly instead of inventing an answer.

Back up each statement with the page it came from, written as a wikilink: [[page-slug]], using the exact slug from that page's "### Page: <slug>" line. Put the citation right after the statement it supports rather than collecting links at the end.`;

/** The user turn: the rendered pages, then the question. */
export function buildAnswerUserPrompt(question: string, pages: PromptPage[]): string {
  return `${formatPagesForPrompt(pages)}\n\n---\n\nQuestion: ${question}`;
}

/** Optional second retrieval pass: ask the model for extra search terms. */
export const KEY_TERMS_SYSTEM_PROMPT =
  'Given a question, list 3 to 8 short search terms or phrases that would help find ' +
  'relevant notes in a keyword search. Drop filler words; favour terms that widen the ' +
  'search rather than narrow it. Reply with only a JSON object: {"terms": ["...", "..."]}.';

export const PECK_ANSWER_MAX_TOKENS = 4096;
export const PECK_KEY_TERMS_MAX_TOKENS = 1024;
export const BROAD_SEARCH_LIMIT = 15;
export const DEFAULT_SEARCH_LIMIT = 10;
